import { Duration } from 'luxon';
import {
  PARAM_LEGS_HOUR_ARRIVAL_BEGIN,
  PARAM_LEGS_HOUR_ARRIVAL_END,
  PARAM_LEGS_HOUR_DEPARTURE_BEGIN,
  PARAM_LEGS_HOUR_DEPARTURE_END,
} from '@optionway/models';

interface LegHoursFilter {
  departureValue: { start: number; end: number };
  arrivalValue: { start: number; end: number };
}

interface HoursFilterStore {
  legHours: LegHoursFilter[];
}

const LEG_HOURS_DEFAULTS: LegHoursFilter = {
  departureValue: { start: 0, end: 24 },
  arrivalValue: { start: 0, end: 24 },
};

export const useHoursFilterStore = defineStore('filters/hours', {
  state: (): HoursFilterStore => ({
    legHours: Array.from({ length: MAX_LEGS }, () => ({
      departureValue: { ...LEG_HOURS_DEFAULTS.departureValue },
      arrivalValue: { ...LEG_HOURS_DEFAULTS.arrivalValue },
    })),
  }),
  getters: {
    committedLegHours(): LegHoursFilter[] {
      return this.legHours.slice(0, Math.max(2, useSearchCriteriaStore().committedCriteriaKey.legs.length));
    },
    earliestDepartureTimeByLeg(): (string | undefined)[] {
      return this.committedLegHours.map((leg) =>
        leg.departureValue.start === LEG_HOURS_DEFAULTS.departureValue.start
          ? undefined
          : Duration.fromObject({ hours: leg.departureValue.start }).toFormat('hh:mm'),
      );
    },
    latestDepartureTimeByLeg(): (string | undefined)[] {
      return this.committedLegHours.map((leg) =>
        leg.departureValue.end === LEG_HOURS_DEFAULTS.departureValue.start ||
        leg.departureValue.end === LEG_HOURS_DEFAULTS.departureValue.end
          ? undefined
          : Duration.fromObject({ hours: leg.departureValue.end }).toFormat('hh:mm'),
      );
    },
    hasActiveFilters(): boolean {
      return this.committedLegHours.some(
        (l) =>
          l.departureValue.start !== LEG_HOURS_DEFAULTS.departureValue.start ||
          l.departureValue.end !== LEG_HOURS_DEFAULTS.departureValue.end ||
          l.arrivalValue.start !== LEG_HOURS_DEFAULTS.arrivalValue.start ||
          l.arrivalValue.end !== LEG_HOURS_DEFAULTS.arrivalValue.end,
      );
    },
    // Zero-width time window: both bounds active (not at their sentinel values) and start >= end.
    isImpossible(): boolean {
      return this.committedLegHours.some(
        (leg) =>
          (leg.departureValue.start > 0 &&
            leg.departureValue.end < 24 &&
            leg.departureValue.start >= leg.departureValue.end) ||
          (leg.arrivalValue.start > 0 && leg.arrivalValue.end < 24 && leg.arrivalValue.start >= leg.arrivalValue.end),
      );
    },
    filterQueryString(): string {
      if (!this.hasActiveFilters) return '';
      const parameters = new URLSearchParams();
      for (const leg of this.committedLegHours) {
        parameters.append(PARAM_LEGS_HOUR_DEPARTURE_BEGIN, String(leg.departureValue.start));
        parameters.append(PARAM_LEGS_HOUR_ARRIVAL_BEGIN, String(leg.arrivalValue.start));
        parameters.append(PARAM_LEGS_HOUR_DEPARTURE_END, String(leg.departureValue.end));
        parameters.append(PARAM_LEGS_HOUR_ARRIVAL_END, String(leg.arrivalValue.end));
      }
      return parameters.toString();
    },
  },
  actions: {
    applyFromUrlQuery(url: string) {
      this.$reset();
      const parameters = new URL(url, 'https://dummy.com').searchParams;
      const departuresStart = parameters.getAll(PARAM_LEGS_HOUR_DEPARTURE_BEGIN);
      const departuresEnd = parameters.getAll(PARAM_LEGS_HOUR_DEPARTURE_END);
      const arrivalsStart = parameters.getAll(PARAM_LEGS_HOUR_ARRIVAL_BEGIN);
      const arrivalsEnd = parameters.getAll(PARAM_LEGS_HOUR_ARRIVAL_END);
      for (const [index, leg] of this.legHours.entries()) {
        applyHourBound(leg.departureValue, 'start', departuresStart[index]);
        applyHourBound(leg.arrivalValue, 'start', arrivalsStart[index]);
        applyHourBound(leg.departureValue, 'end', departuresEnd[index]);
        applyHourBound(leg.arrivalValue, 'end', arrivalsEnd[index]);
      }
    },
  },
});

function applyHourBound(window: { start: number; end: number }, bound: 'start' | 'end', raw?: string): void {
  const value = Number.parseInt(raw ?? '', 10);
  if (!Number.isNaN(value)) window[bound] = Math.min(Math.max(value, 0), 24);
}
